import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const Auth = () => {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "signup";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        await register(form.name, form.email, form.password);
      } else {
        await login(form.email, form.password);
      }
      toast.success(mode === "signup" ? "Welcome to Anchor." : "Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--bg)" }}>
      <div
        className="hidden lg:flex flex-1 relative items-end p-12"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1737040455054-f83c122176ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHdhdGVyY29sb3IlMjBzYW5kfGVufDB8fHx8MTc3Njg5Mzk1NXww&ixlib=rb-4.1.0&q=85")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div
          className="relative max-w-md p-8 rounded-3xl border backdrop-blur-xl"
          style={{
            backgroundColor: "rgba(253, 251, 247, 0.82)",
            borderColor: "var(--border)",
          }}
        >
          <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--muted)" }}>
            A quiet welcome
          </p>
          <p className="font-serif text-3xl leading-tight" style={{ color: "var(--ink)" }}>
            "The only way out is through — and you don't have to go through it alone."
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 md:px-16 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-sm mb-10 hover:opacity-70"
          style={{ color: "var(--ink-soft)" }}
          data-testid="auth-back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Link>

        <div className="max-w-md w-full">
          <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--muted)" }}>
            {mode === "signup" ? "Create your account" : "Welcome back"}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl mb-2" style={{ color: "var(--ink)" }}>
            {mode === "signup" ? "Begin, gently." : "Sign in."}
          </h1>
          <p className="mb-8" style={{ color: "var(--ink-soft)" }}>
            {mode === "signup"
              ? "A few details so your notes, plans, and conversations are here waiting."
              : "Pick up where you left off."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "signup" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--ink-soft)" }}>
                  Name
                </Label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 h-12 rounded-xl bg-white"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--ink-soft)" }}>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 h-12 rounded-xl bg-white"
                style={{ borderColor: "var(--border)" }}
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--ink-soft)" }}>
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 h-12 rounded-xl bg-white"
                style={{ borderColor: "var(--border)" }}
                data-testid="auth-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full text-white text-base"
              style={{ backgroundColor: "var(--terracotta)" }}
              data-testid="auth-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "signup" ? "Create account" : "Sign in"}
            </Button>
          </form>

          <p className="text-sm mt-8" style={{ color: "var(--ink-soft)" }}>
            {mode === "signup" ? "Already have an account?" : "New here?"}{" "}
            <button
              onClick={() => setMode(mode === "signup" ? "login" : "signup")}
              className="underline"
              style={{ color: "var(--terracotta)" }}
              data-testid="auth-toggle-mode"
            >
              {mode === "signup" ? "Sign in" : "Create an account"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
